import { useMemo } from 'react';
import { TextField, FunctionField, RadioButtonGroupInput, SelectInput, SimpleShowLayout, usePermissions, WrapperField, useTranslate } from 'react-admin';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useDeviceStates } from '@/constants/DeviceStates.jsx';
import { DefaultBulkActionButtons } from '@/components/global/buttons/bulkButtons';
import { SearchBar } from '@/components/global/list/searchBar';
import { BooleanFilter, PeriodFilter, ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import ListCollectionPointField from '@/components/collectionPoint/ListCollectionPointField.jsx';
import { useDrawer } from '@/composables/useDrawer';
import DeviceListServiceStatusField from '@/components/device/DeviceListServiceStatusField.jsx';
import DeviceBinStateField from '@/components/device/DeviceBinStateField.jsx';
import DeviceListActions from '@/components/device/DeviceListActions.jsx';
import IsActiveField from '@/components/global/fields/IsActiveField.jsx';
import DeviceUptimeField from '@/components/device/DeviceUptimeField.jsx';
import { DeviceStatisticList } from '@/components/analytics/statisticList.jsx';
import FromToFilterComponent from '@/components/global/filters/FromToFilterComponent.jsx';
import ContractorField from '@/components/contractors/ContractorField.jsx';
import DeviceMoreActionsPopover from '@/components/device/DeviceMoreActionsPopover.jsx';
import { collectionTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { DeviceStateField } from '@/components/device/fields/DeviceStateField.jsx';
import { DeviceBulkForm } from '@/components/device/forms/DeviceBulkForm.jsx';
import { BagsClickable, TransactionsClickable } from '@/components/device/fields/DeviceClickableFields.jsx';
import { getDeviceModelFilterOptionText, getDeviceModelSearchFilter } from '@/helpers/parameter/parameterSubsetDisplay.js';

export { DeviceEditContent } from '@/components/device/DeviceEditContent.jsx';
export { DeviceStateField };

export const DeviceList = () => {
    const { isLoading, permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();
    const deviceStates = useDeviceStates();

    const hasContractorField = permissions.isAdmin || permissions.isBok || permissions.isPosOwner;
    const isService = permissions.isService;

    const filters = useMemo(() => {
        const serviceStatusFilterChoices = [
            { id: 'ok', name: translate('resources.device.fields.serviceStatusFilter.onlyOk') },
            { id: 'serviced', name: translate('resources.device.fields.serviceStatusFilter.onlyServiced') },
            { id: 'all', name: translate('common.all') },
        ];

        return [
            <SearchBar key='search' source='value' label={translate('common.search')} alwaysOn />,
            <PeriodFilter key='period' alwaysOn={true} className='period-filter' />,
            hasContractorField && (
                <ReferenceArrayFilter
                    key='contractor'
                    source='contractorId_in'
                    reference='contractor'
                    label={translate('common.contractor')}
                    forceDesktopOnMobile={true}
                    alwaysOn={isMobile}
                />
            ),
            <ReferenceArrayFilter
                key='collectionPoint->id_in'
                source='collectionPoint->id_in'
                label={translate('common.collectionPoint')}
                optionText={choice => choice.customId}
                reference='collection-point'
                searchFields={['customId']}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='uptime'
                source='analytics->uptime_range'
                label={translate('common.uptime')}
                type='percent'
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='transactionCount'
                source='analytics->transactionsCount_range'
                label={translate('common.transactionsCount')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='bagCount'
                source='analytics->bagsCount_range'
                label={translate('common.bagsCount')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <BooleanFilter key='isActive' source='isActive' label={translate('common.active')} forceDesktopOnMobile={true} alwaysOn={isMobile} />,
            <RadioButtonGroupInput
                key='serviceStatus'
                source='serviceStatus_map'
                label={translate('resources.device.fields.serviceStatus')}
                choices={serviceStatusFilterChoices}
                defaultValue={isMobile ? false : 'all'}
                className='radio-button-group'
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='groupId_in'
                source='groupId_in'
                reference='device-group'
                label={translate('common.deviceGroup')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='modelId_in'
                source='modelId_in'
                reference='device-model'
                label={translate('common.model')}
                optionText={getDeviceModelFilterOptionText}
                getSearchFilter={getDeviceModelSearchFilter}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <SelectInput
                key='state'
                source='state'
                label={translate('common.status')}
                choices={deviceStates.map(state => Object.fromEntries(Object.entries(state).filter(([key]) => key !== 'icon' && key !== 'tooltip')))}
                emptyText={translate('common.all')}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='city'
                source='collectionPoint->address->city_in'
                reference='city'
                label={translate('common.city')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <SelectInput
                key='collectionType'
                source='collectionType'
                label={translate('resources.device.fields.collectionType')}
                choices={collectionTypeChoices(translate)}
                emptyText={translate('common.all')}
                alwaysOn={isMobile}
            />,
        ];
    }, [isMobile, hasContractorField, translate, deviceStates]).filter(Boolean);

    if (isLoading) return null;

    const bulkActionButtons = permissions.isAdmin ? (
        <DefaultBulkActionButtons buttons={[<DeviceBulkForm key='BulkForm' isAdmin />]} hasRestart hasReturnToNew />
    ) : (
        <DeviceBulkForm isAdmin={false} />
    );

    const transformFilter = filter => {
        const newFilter = { ...filter };
        if (newFilter['analytics->uptime_gte'] != null) {
            newFilter['analytics->uptime_gte'] = Number(newFilter['analytics->uptime_gte']) * 100;
        }
        if (newFilter['analytics->uptime_lte'] != null) {
            newFilter['analytics->uptime_lte'] = Number(newFilter['analytics->uptime_lte']) * 100;
        }
        return newFilter;
    };

    return (
        <CustomList
            filters={filters}
            filter={{ deploymentStatus: 'deployed', withAggregates: true }}
            filterDefaultValues={{}}
            transformFilter={transformFilter}
            addLastColumnShadow={true}
            actions={<DeviceListActions />}
        >
            <DeviceStatisticList />
            <CustomDatagridConfigurable
                bulkActionButtons={bulkActionButtons}
                rowClick={id => {
                    openDrawer(id, 'device-show');
                    return false;
                }}
                omit={['collectionType']}
            >
                {hasContractorField ? (
                    <FunctionField
                        label={translate('common.contractor')}
                        sortBy='contractorName'
                        render={record => <ContractorField contractor={record.group?.contractor} />}
                    />
                ) : null}
                <FunctionField
                    source='collectionPoint.id'
                    label={translate('common.collectionPoint')}
                    sortBy='collectionPoint.customId'
                    render={record => (
                        <ListCollectionPointField
                            collectionPoint={record.collectionPoint}
                            sx={{ cursor: 'pointer' }}
                            onClick={e => {
                                e.stopPropagation();
                                openDrawer(record.collectionPoint.id, 'collection-point-show');
                            }}
                        />
                    )}
                />
                <FunctionField source='analytics.uptime' label={translate('common.uptime')} render={record => <DeviceUptimeField record={record} />} />
                <FunctionField
                    source='analytics.transactionsCount'
                    label={translate('common.transactionsCount')}
                    render={record => <TransactionsClickable record={record} />}
                />
                <FunctionField source='analytics.bagsCount' label={translate('common.bagsCount')} render={record => <BagsClickable record={record} />} />
                <FunctionField
                    source='binState'
                    label={translate('resources.device.fields.binState')}
                    render={record => <DeviceBinStateField record={record} />}
                />
                <FunctionField
                    label={translate('resources.device.fields.serviceStatus')}
                    sortBy='serviceStatus'
                    render={record => <DeviceListServiceStatusField record={record} />}
                />
                {!isService ? (
                    <FunctionField
                        label={translate('resources.device.fields.group')}
                        sortBy='group.name'
                        render={record => (
                            <Box
                                onClick={e => {
                                    e.stopPropagation();
                                    openDrawer(record.group.id, 'device-group-show');
                                }}
                                className='list-group'
                                label={record.group.name}
                            >
                                {record.group.name}
                            </Box>
                        )}
                    />
                ) : (
                    <TextField source='group.name' label={translate('resources.device.fields.group')} />
                )}
                <TextField source='serialNumber' label={translate('resources.device.fields.serialNumber')} />
                <FunctionField label={translate('common.status')} render={record => <DeviceStateField record={record} />} sortBy='state' />
                <FunctionField label={translate('common.active')} render={record => <IsActiveField record={record} />} sortBy='isActive' />
                <FunctionField
                    label={translate('resources.device.fields.collectionType')}
                    sortBy='collectionType'
                    source='collectionType'
                    render={record => {
                        return translate(`resources.device.choices.collectionType.${record?.collectionType}`);
                    }}
                />
                <WrapperField source='softwareSoc' label={translate('menu.software')} sortBy='software.softwareSoc'>
                    <Box display='flex'>
                        <SimpleShowLayout flex={1}>
                            <TextField source='softwareMcu' label={translate('resources.device.labels.mcu')} />
                        </SimpleShowLayout>
                        <SimpleShowLayout flex={1}>
                            <TextField source='softwareSoc' label={translate('resources.device.labels.soc')} />
                        </SimpleShowLayout>
                    </Box>
                </WrapperField>
                <FunctionField
                    label={translate('common.options')}
                    onClick={e => e.stopPropagation()}
                    render={record => <DeviceMoreActionsPopover record={record} />}
                />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};
